import { themeAtom } from "../../shared/atoms";
import Datepicker from "../../shared/components/Datepicker/Datepicker";
import { countries } from "../../shared/const/countries.const";
import { hullTypes } from "../../shared/const/hull-types.const";
import { ports } from "../../shared/const/ports.const";
import { otherVesselTypes, vesselTypes } from "../../shared/const/vessel-types.const";
import { Bq1GeneralInformationM } from "../models/bq1-general-information.model";
import { formDate } from "../services/baltic-forms.utils";
import { useAtomValue } from "jotai";
import React from "react";
import { useParams } from "react-router-dom";

export function Bq1GenInfo(
  form: Bq1GeneralInformationM,
  updateForm: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
  updateDate: (date: Date | null, field: string) => void,
  toggleComments: (inputId: string) => void,
  isCommentsVisible: (inputId: string) => boolean,
  readonly: boolean,
) {
  const { vesselName, imoNumber } = useParams();
  const theme = useAtomValue(themeAtom);

  return (
    <div>
      {/* Vessel Name */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Vessel name</label>
          <input
            type="text"
            disabled
            name="vesselName"
            value={vesselName}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          />
        </div>

        {/* LR/IMO Number */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">LR/IMO Number</label>
          <input
            type="text"
            disabled
            name="lrImoNumber"
            value={imoNumber}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          />
        </div>
      </div>

      {/* Last previous name */}
      <div className="mb-2 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Last previous name</label>
          <input
            disabled={readonly}
            type="text"
            name="lastPreviousName"
            value={form.lastPreviousName}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          />
        </div>
        <div className="col-span-1">
          <label className="block text-sm font-bold">Date of change</label>
          <Datepicker
            disabled={readonly}
            selected={formDate(form.lastPreviousNameDateChange)}
            onChange={(date) => updateDate(date, "lastPreviousNameDateChange")}
          />
        </div>
      </div>

      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("lastPreviousNameComments")}
            >
              Comments
            </button>
          </label>
          {(form.lastPreviousNameComments || isCommentsVisible("lastPreviousNameComments")) && (
            <textarea
              disabled={readonly}
              name="lastPreviousNameComments"
              value={form.lastPreviousNameComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Second last previous name */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Second last previous name</label>
          <input
            disabled={readonly}
            type="text"
            name="secondLastPreviousName"
            value={form.secondLastPreviousName}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          />
        </div>
        <div className="col-span-1">
          <label className="block text-sm font-bold">Date of change</label>
          <Datepicker
            disabled={readonly}
            selected={formDate(form.secondLastPreviousNameDateChange)}
            onChange={(date) => updateDate(date, "secondLastPreviousNameDateChange")}
          />
        </div>
      </div>

      {/* Third last previous name */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Third last previous name</label>
          <input
            disabled={readonly}
            type="text"
            name="thirdLastPreviousName"
            value={form.thirdLastPreviousName}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          />
        </div>
        <div className="col-span-1">
          <label className="block text-sm font-bold">Date of change</label>
          <Datepicker
            disabled={readonly}
            selected={formDate(form.thirdLastPreviousNameDateChange)}
            onChange={(date) => updateDate(date, "thirdLastPreviousNameDateChange")}
          />
        </div>
      </div>

      {/* Fourth last previous name */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Fourth last previous name</label>
          <input
            disabled={readonly}
            type="text"
            name="fourthLastPreviousName"
            value={form.fourthLastPreviousName}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          />
        </div>
        <div className="col-span-1">
          <label className="block text-sm font-bold">Date of change</label>
          <Datepicker
            disabled={readonly}
            selected={formDate(form.fourthLastPreviousNameDateChange)}
            onChange={(date) => updateDate(date, "fourthLastPreviousNameDateChange")}
          />
        </div>
      </div>

      {/* Flag */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Flag</label>
          <select
            disabled={readonly}
            name="flag"
            value={form.flag}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          >
            <option value=""></option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Port of Registry */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Port of Registry</label>
          <select
            disabled={readonly}
            name="portOfRegistry"
            value={form.portOfRegistry}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          >
            <option value=""></option>
            {ports.map((port) => (
              <option key={port} value={port}>
                {port}
              </option>
            ))}
          </select>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("portOfRegistryComments")}
            >
              Comments
            </button>
          </label>
          {(form.portOfRegistryComments || isCommentsVisible("portOfRegistryComments")) && (
            <textarea
              disabled={readonly}
              name="portOfRegistryComments"
              value={form.portOfRegistryComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Ship Type */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Ship Type</label>
          <select
            disabled={readonly}
            name="shipType"
            value={form.shipType}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          >
            <option value=""></option>
            {vesselTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Type of ship if other */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Type of ship (if other)</label>
          <select
            disabled={readonly}
            name="typeOfShipIfOther"
            value={form.typeOfShipIfOther}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          >
            <option value=""></option>
            {otherVesselTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Type of Hull*/}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Type of Hull</label>
          <select
            disabled={readonly}
            name="typeOfHull"
            value={form.typeOfHull}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          >
            <option value=""></option>
            {hullTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
